import reactUpdate, {Spec} from 'immutability-helper';
import $ from 'jquery';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import * as benchlingUtil from 'BenchlingUtil';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {EventConstants} from 'constants/EventConstants';
import {FolderSummary} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {RegistryLabelingStrategy} from 'constants/RegistryConstants';
import * as context from 'Context';
import EntryDAO from 'dao/EntryDAO';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import AppDispatcher from 'lib/Dispatcher';
import * as DataTableActions from 'notebook/data-tables/DataTableActions';
import {
  Entry,
  EntryProtocol,
  EntryVersion,
  EntryWithFulfillments,
  IndexSlice,
  ExternalProtocol,
  NoteReference,
  ToServerEntry,
  BlobDetail,
  FromServerProtocol,
} from 'notebook/ELNConstants';
import * as ExportActions from 'notebook/entries/ExportActions';
import {getNoteTableMap} from 'notebook/mediocre/util/TableMapUtil';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {
  RegistrationTable,
  RegistrationTableType,
} from 'notebook/registration-tables/RegistrationTableConstants';
import {ReviewSummary} from 'notebook/reviews/ReviewConstants';
import * as ReviewDAO from 'notebook/reviews/ReviewDAO';
import {ACTION_RESET_VERSION} from 'notebook/store/VersioningConstants';
import {StructuredTableMapping} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import {evaluateTablesWithResolutions} from 'notebook/tables/spreadsheets/SpreadsheetEvalUtil';
import {FocusedTableViewMode} from 'notebook/tables/TableConstants';
// @ts-expect-error
import EntryUtil from 'notebook/util/EntryUtil';
// @ts-expect-error
import EntryVersionUtil from 'notebook/util/EntryVersionUtil';
import {EntryNote} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import * as popup from 'Popup';
import DocumentEditorLockUtil from 'realtime/util/DocumentEditorLockUtil';
import * as ModelActions from 'redux-stores/ModelActions';
import {getStore} from 'redux-stores/Store';
import {RequestFulfillmentDefinition} from 'requests/fulfillments/FulfillmentConstants';
import * as FulfillmentReduxActions from 'requests/fulfillments/FulfillmentReduxActions';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import {StudyAssociation} from 'studies/StudyableDAO';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import licenses from 'user/Licenses';
import APIUtil from 'util/APIUtil';
import * as logging from 'util/Logging';
import * as net from 'util/Net';
import pick from 'util/pick';
import {RegistrationRecordList} from 'workflows/WorkflowConstants';

export function dispatchFulfillmentsLoadedForEntry(entry: EntryWithFulfillments): void {
  const {requestFulfillments} = entry;
  getStore().dispatch(
    FulfillmentReduxActions.fulfillmentsLoadedForEntry({
      entryId: entry.id,
      requestFulfillments,
    })
  );
}

function dispatchNoteReferencesLoadedForEntry(entryId: string, noteReferences?: Array<NoteReference>): void {
  getStore().dispatch(ExportActions.noteReferencesLoaded(entryId, noteReferences));
}

function dispatchBlobDetailsLoadedForEntry(entryId: string, blobDetails?: Array<BlobDetail>): void {
  getStore().dispatch(ExportActions.blobDetailsLoaded(entryId, blobDetails));
}

interface EntryOpenOptions {
  versionId?: number | null;
  // Allows the document to be immediately patched if evaluation runs on open.
  bumpVersion?: boolean;
  deferred?: JQueryDeferred<{}>;
}

const EntryActions = {
  // protocolCids can be an array of protocolCids, or omitted to print all protocols in the entry
  print: (
    entryId: string,
    param?: {
      showNote?: boolean;
      protocolCids?: Array<string>;
      indexSlice?: IndexSlice | null;
      showTimestamps?: boolean;
    }
  ): void => {
    let p: EntryProtocol;
    if (param == null) {
      param = {};
    }
    // eslint-disable-next-line prefer-const
    let {showNote, protocolCids, indexSlice, showTimestamps} = param;
    const EntryStore = require('stores/EntryStore').default;
    const state = EntryStore.getEditorState(entryId);
    const {entryProtocols} = state.entry;
    if (protocolCids == null) {
      protocolCids = (() => {
        const result = [];
        for (p of Array.from(entryProtocols as Array<EntryProtocol>)) {
          result.push(p.cid);
        }
        return result;
      })();
    }
    const entryProtocolCids: Array<string> = protocolCids;

    function handlePrint(): void {
      if (showNote == null) {
        showNote = false;
      }

      const versionId = state.openVersionId;
      let args = `versionId=${versionId}`;
      if (showNote) {
        args += '&note=true';
      }
      if (showTimestamps) {
        args += '&showTimestamps=true';
      }
      const protocolIds = Array.from(entryProtocolCids).map((cid: string) => state.cidIdMap[cid]);
      args += `&protocols=${protocolIds.join(',')}`;
      if (indexSlice != null) {
        args += `&startIndex=${indexSlice.start}&endIndex=${indexSlice.end}`;
      }
      window.open(`view?${args}#print`, '_blank');
    }

    // eslint-disable-next-line no-restricted-syntax
    const externalProtocols = _.filter(
      entryProtocols,
      (ep: EntryProtocol) => ep.externalFile != null && Array.from(entryProtocolCids).includes(ep.cid)
    );
    if (externalProtocols.length > 0) {
      const message = (
        <div>
          <p>This entry contains imported protocols which cannot be printed:</p>
          <ul>
            {Array.from(externalProtocols).map((protocol) => (
              <li>{protocol.name}</li>
            ))}
          </ul>
          <p>Download and open these protocols directly to print them.</p>
        </div>
      );
      // It's only safe to use renderToStaticMarkup because formatSelection gets used in a non-React context.
      // See https://github.com/facebook/react/issues/3344
      new popup.Confirm({
        title: 'Print Entry',
        msg: ReactDOMServer.renderToStaticMarkup(message),
      })
        .choice('Print Entry', 'btn-af-primary', handlePrint)
        .choice('Cancel', 'btn-af-default')
        .show();
    } else {
      handlePrint();
    }
  },

  updateProtocol: (entryId: string, entry: Entry, protocolCid: string, op: Spec<EntryProtocol>): void => {
    EntryActions.update(
      entryId,
      EntryUtil.entryVersionUpdate({
        $apply(entryVersion: EntryVersion): EntryVersion {
          return EntryVersionUtil.withEntryProtocolUpdated(entryVersion, protocolCid, op);
        },
      })
    );
  },

  addProtocol: (entryId: string, entry: Entry, protocol: ExternalProtocol | FromServerProtocol): void => {
    const cidIdMap = {};
    const entryUpdate = EntryUtil.entryVersionUpdate({
      $apply(entryVersion: EntryVersion): EntryVersion {
        return EntryVersionUtil.withProtocolAdded(entryVersion, protocol, cidIdMap);
      },
    });
    EntryActions.update(entryId, entryUpdate, {cidIdMap});
  },

  removeProtocol: (entryId: string, entry: Entry, epCid: string): void => {
    EntryActions.update(
      entryId,
      EntryUtil.entryVersionUpdate({
        $apply(entryVersion: EntryVersion): EntryVersion {
          return EntryVersionUtil.withEntryProtocolRemoved(entryVersion, epCid);
        },
      })
    );
  },

  revertProtocol: (
    entryId: string,
    entry: Entry,
    revertedEntryProtocol: EntryProtocol,
    restoreData: SelectionRestoreData
  ): void => {
    const revertedEntry = reactUpdate(
      entry,
      EntryUtil.entryVersionUpdate({
        $apply(entryVersion: EntryVersion): EntryVersion {
          return EntryVersionUtil.withEntryProtocolReverted(entryVersion, revertedEntryProtocol);
        },
      })
    );
    EntryActions.revert(entryId, revertedEntry, restoreData);
  },

  reorderProtocols: (entryId: string, entry: Entry, cids: Array<string>): void => {
    EntryActions.update(
      entryId,
      EntryUtil.entryVersionUpdate({
        $apply(entryVersion: EntryVersion): EntryVersion {
          return EntryVersionUtil.withEntryProtocolsReordered(entryVersion, cids);
        },
      })
    );
  },

  registerObjects: (entryId: string, cidIdMap: CidIdMap): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.ENTRY_REGISTER_OBJECTS,
      entryId,
      cidIdMap,
    });
  },

  updateNote: (
    entryId: string,
    note: EntryNote,
    opts: {
      performTableEvaluation?: boolean;
      updatedItemPartIndexes?: Array<number>;
      canIncrementalSave?: boolean;
    } = {
      performTableEvaluation: true,
    },
    structuredTableMappings: Array<StructuredTableMapping> | undefined = undefined
  ): void => {
    EntryActions.update(
      entryId,
      EntryUtil.entryVersionUpdate({
        $merge: {
          note,
          ...(structuredTableMappings && {
            structuredTableMappings,
          }),
        },
      }),
      opts
    );
  },

  setName: (entryId: string, name: string): void => {
    EntryActions.update(entryId, {$merge: {name}});
  },

  update: (
    entryId: string,
    op: Spec<Entry>,
    {
      cidIdMap = {},
      bumpVersion = true,
      performTableEvaluation = true,
      updatedItemPartIndexes,
      canIncrementalSave,
    }: Partial<{
      cidIdMap: {[cid: string]: string};
      bumpVersion: boolean;
      performTableEvaluation: boolean;
      updatedItemPartIndexes?: Array<number>;
      canIncrementalSave?: boolean;
    }> = {}
  ): void => {
    // Attempt to acquire an editor lock prior to persisting the new entry version
    DocumentEditorLockUtil.acquireLock(entryId);

    // Apply op to the entry specified by entryId.
    AppDispatcher.dispatch({
      actionType: EventConstants.ENTRY_UPDATE,
      entryId,
      op,
      cidIdMap,
      bumpVersion,
      performTableEvaluation,
      updatedItemPartIndexes,
      canIncrementalSave,
    });
  },

  postServerUpdate: (entryId: string, data: Partial<Entry>): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.ENTRY_UPDATE,
      entryId,
      op: {$merge: data},
      bumpVersion: false,
    });
  },

  postServerSideUpdate: (entryId: string): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.ENTRY_SERVER_SIDE_UPDATE,
      entryId,
      bumpVersion: false,
    });
  },

  revert: (
    entryId: string,
    // Keep in sync with EntryVersionUtil.pickRevertableFields and EntryUtil.pickRevertableFields
    entry: Partial<Pick<Entry, 'name' | 'note' | 'entryProtocols' | 'structuredTableMappings'>>,
    restoreData: SelectionRestoreData
  ): void => {
    // Replace the open entry with the specified data.
    // Useful to reset the state to an older state.
    AppDispatcher.dispatch({
      actionType: EventConstants.ENTRY_REVERT,
      entryId,
      entry,
      restoreData,
    });
  },

  // TODO(T1135): Don't pass in deferred. Note: Only ProxyFile should be passing in deferred.
  loadVersion: (entryId: string, versionId: number, param?: {deferred?: JQueryDeferred<{}>}): void => {
    if (param == null) {
      param = {};
    }
    const {deferred} = param;
    AppDispatcher.dispatch({
      actionType: EventConstants.ENTRY_LOAD_VERSION,
      entryId,
      versionId,
    });
    EntryDAO.getVersion(entryId, versionId)
      .done((response: EntryWithFulfillments) => {
        dispatchFulfillmentsLoadedForEntry(response);
        if (response.noteReferences) {
          dispatchNoteReferencesLoadedForEntry(entryId, response.noteReferences);
        }
        if (response.blobDetails) {
          dispatchBlobDetailsLoadedForEntry(entryId, response.blobDetails);
        }
        AppDispatcher.dispatch({
          actionType: EventConstants.ENTRY_LOAD_VERSION_COMPLETED,
          entryId,
          versionId,
          version: response,
        });
        if (deferred != null) {
          deferred.resolve();
        }
      })
      .fail((response: Response) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.ENTRY_LOAD_VERSION_FAILED,
          entryId,
          versionId,
        });
        if (deferred != null) {
          deferred.reject(response);
        }
      });
  },

  fetchVersions: (entryId: string): void => {
    // Look up the currently open version to ensure that it is not coarsened but always returned in the
    // results of the fetch.
    const EntryStore = require('stores/EntryStore').default;
    if (!EntryStore.inStore(entryId)) {
      return;
    }
    const {openVersionId} = EntryStore.getEditorState(entryId);

    AppDispatcher.dispatch({
      actionType: EventConstants.ENTRY_FETCH_VERSIONS,
      entryId,
    });
    EntryDAO.getVersions(entryId, {openVersionId})
      .done((response) => {
        AppDispatcher.dispatch(
          // eslint-disable-next-line no-restricted-syntax
          _.extend(
            {
              actionType: EventConstants.ENTRY_FETCH_VERSIONS_COMPLETED,
              entryId,
              versions: response!.data,
            },
            pick(response!, 'parent')
          )
        ); // parent key present with null value: the parent was deleted
        // omitted parent key: there is no parent.
      })
      .fail(() => {
        AppDispatcher.dispatch({
          actionType: EventConstants.ENTRY_FETCH_VERSIONS_FAILED,
          entryId,
        });
      });
  },

  delete: (entryId: string, param?: {success?: () => void}): void => {
    if (param == null) {
      param = {};
    }
    const {success} = param;
    AppDispatcher.dispatch({
      actionType: EventConstants.ENTRY_DELETE,
      entryId,
    });
    EntryDAO.delete(entryId)
      .done(() => {
        AppDispatcher.dispatch({
          actionType: EventConstants.ENTRY_DELETE_COMPLETED,
          entryId,
        });
        if (typeof success === 'function') {
          success();
        }
      })
      .fail((response: Response) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.ENTRY_DELETE_FAILED,
          entryId,
          error: response,
        });
        notifications.notifyError(response);
      });
  },

  refreshReview: (entryId: string): void => {
    const EntryStore = require('stores/EntryStore').default;
    const {entry} = EntryStore.getEditorState(entryId);
    // entry can be null if QuickEditModal is closed and re-open starts before save comes back
    // TODO(T1847): remove after adding refcounts
    if ((entry != null ? entry.review : undefined) == null) {
      return;
    }

    assert(licenses.hasLicense('LICENSE_REVIEWS'), 'Expected to have LICENSE_REVIEWS');
    ReviewDAO.fetchReview(entry.review.id).then((review: ReviewSummary) => {
      if (EntryStore.inStore(entryId)) {
        EntryActions.postServerUpdate(entryId, {review});
      }
    });
  },

  /**
   * Update non-versioned fields (name, folder) via an immediate request that is processed
   * non-optimistically.
   *
   * @param  {String} entryId
   * @param  {Object} An object containing:
   *                    data: {name, folder_id}
   *                    success: an optional callback fired on success
   */
  saveFileData: (
    entryId: string,
    {data, success}: {data: {name?: string; folder_id?: string}; success?: () => void}
  ): void => {
    EntryDAO.update(entryId, data)
      .done((response) => {
        const mergeData: {folder: FolderSummary; editURL: string; name?: string} = {
          folder: response!.folder,
          editURL: response!.editURL,
        };
        if (data.name != null) {
          mergeData.name = data.name;
        }
        if (response!.errors) {
          response!.errors.forEach((error) => {
            notifications.error(error);
          });
        }
        EntryActions.postServerUpdate(entryId, mergeData);
        if (typeof success === 'function') {
          success();
        }
      })
      .fail(notifications.notifyError);
  },

  resetToOpenVersion: (entryId: string): void => {
    const EntryStore = require('stores/EntryStore').default;
    const {isLatest} = EntryStore.getEditorState(entryId);
    assert(!isLatest, 'Unexpected reset version while on latest version');

    context.using({immediateAutoSaverQueueSaveAction: ACTION_RESET_VERSION}, this, () => {
      AppDispatcher.dispatch({
        actionType: EventConstants.ENTRY_RESET_TO_OPEN_VERSION,
        entryId,
      });
    });
  },

  queueSave: (entryId: string): void | JQueryPromise<void> => {
    const EntryStore = require('stores/EntryStore').default;
    return EntryStore.queueSave(entryId);
  },

  /**
   * Save the open entry version via a throttled request.
   * @return (Promise) a Promise which will be resolved once the save mechanism completes.  The boolean resolved
   * by the Promise will contain a `true` if the save was actually performed, or a `false` if the save was not
   * done, due to no modifications detected on the entry or the entry being locked.
   */
  save: (entryId: string): JQueryPromise<boolean> => {
    const EntryStore = require('stores/EntryStore').default;
    /**
     * Provide a snapshot of the relevant entry state found in the store for performing a save.
     * This should be called on both the leading and trailing edge of the throttled save so that we can
     * save the latest version of the entry if it is still in the store, or perform a best-effort save if
     * the entry was removed from the store before the trailing edge was hit.
     *
     * @return {Object} a snapshot of the entry store state:
     *   - openVersion: what to set as the savedVersion on completed save
     *   - serverEntry: a server-friendly representation for patching the entry
     *   - clientEntry:
     *       The client representation of the entry (which corresponds to the serverEntry).
     *       This is useful for the client to perform operations on the persisted entry post-save.
     */
    function getStoreSnapshotForSave(): {
      openVersion: number;
      serverEntry: ToServerEntry;
      clientEntry: Entry;
    } {
      const {entry, openVersion, metadata} = EntryStore.getEditorState(entryId);
      // eslint-disable-next-line no-restricted-syntax
      const serverEntry = _.extend(EntryStore.prepareForSave(entryId, entry), {metadata});
      return {openVersion, serverEntry, clientEntry: entry};
    }

    // This is saved here in case the entry is removed from store before save finishes.
    // TODO(T1847): remove after adding refcounts
    const dataAtSaveTime = getStoreSnapshotForSave();

    // Create a deferred promise that will be resolved once the save has completed.
    const deferredPromise = $.Deferred<boolean>();
    benchlingUtil.asyncThrottle(`entry-save-${entryId}`, () => {
      let dataToUse;
      if (EntryStore.inStore(entryId)) {
        // Avoid doing a save if the entry has been locked.
        if (EntryStore.isLockedFromExternalChange(entryId)) {
          return deferredPromise.resolve(false).promise();
        }
        // Avoid doing a save if the entry is already up-to-date and no additional data provided.
        if (!EntryStore.hasUnsavedChanges(entryId)) {
          return deferredPromise.resolve(false).promise();
        }

        dataToUse = getStoreSnapshotForSave();
      } else {
        dataToUse = dataAtSaveTime;
      }

      const {openVersion, serverEntry, clientEntry} = dataToUse;
      AppDispatcher.dispatch({
        actionType: EventConstants.ENTRY_SAVE,
        entryId,
      });
      return EntryDAO.update(entryId, serverEntry)
        .done((response) => {
          AppDispatcher.dispatch({
            actionType: EventConstants.ENTRY_SAVE_COMPLETED,
            entryId,
            savedVersion: openVersion,
            versionId: response!.cidIdMap[serverEntry.cid],
            cidIdMap: response!.cidIdMap,
            cidNotePartProvenanceIdMap: response!.cidNotePartProvenanceIdMap,
            serverData: {
              folder: response!.folder,
              editURL: response!.editURL,
            },
            lock: response!.lock,
          });
          if (response!.errors) {
            response!.errors.forEach((error) => {
              notifications.error(error);
            });
          }
          if (EntryStore.inStore(entryId)) {
            DataTableActions.fetchDataTablesValidationStatus(entryId, clientEntry.note);
            if (licenses.hasLicense('LICENSE_REVIEWS')) {
              EntryActions.refreshReview(entryId);
            }
          }
          // Resolve the deferred promise to signal that the save has completed.
          deferredPromise.resolve(true);
        })
        .fail((response: Response) => {
          EntryStore.markAllForUpdate(entryId);
          notifications.stickyNotifyForFriendlyError(response, 'entry-save-fail');
          AppDispatcher.dispatch({
            actionType: EventConstants.ENTRY_SAVE_FAILED,
            entryId,
            error: response,
          });
          // Resolve the deferred promise to signal that the save has completed.
          deferredPromise.resolve(false);
        });
    });
    // Return the deferred promise so that the caller can wait for the save to complete.
    return deferredPromise.promise();
  },

  // Returns true if the entry is successfully fetched from the server, false if the server returns an error.
  // TODO(T1135): Remove deferred param option. Note: Only ProxyFile should be passing in deferred.
  open: async (entryId: string, param?: EntryOpenOptions): Promise<boolean> => {
    let xhr;
    let didSucceed = false;
    if (param == null) {
      param = {};
    }
    const {versionId, deferred, bumpVersion} = param;
    const EntryStore = require('stores/EntryStore').default;

    AppDispatcher.dispatch({
      actionType: EventConstants.ENTRY_OPEN,
      entryId,
    });
    if (versionId != null) {
      xhr = EntryDAO.getVersion(entryId, versionId);
    } else {
      xhr = EntryDAO.get(entryId);
    }
    try {
      const response = await Promise.resolve(xhr);
      dispatchFulfillmentsLoadedForEntry(response!);
      if (response!.noteReferences) {
        dispatchNoteReferencesLoadedForEntry(entryId, response!.noteReferences);
      }
      if (response!.blobDetails) {
        dispatchBlobDetailsLoadedForEntry(entryId, response!.blobDetails);
      }
      AppDispatcher.dispatch({
        actionType: EventConstants.ENTRY_OPEN_COMPLETED,
        entryId,
        entry: response,
        bumpVersion,
      });
      if (EntryStore.inStore(entryId)) {
        const clientEntry = EntryStore.getEntry(entryId);
        DataTableActions.fetchDataTablesValidationStatus(entryId, clientEntry.note);
        if (bumpVersion) {
          AppDispatcher.dispatch({
            actionType: EventConstants.ENTRY_UPDATE,
            entryId,
            op: {$merge: {}}, // NOOP
            bumpVersion: true,
          });
        }
      }
      if (deferred != null) {
        deferred.resolve();
      }
      didSucceed = true;
    } catch (err) {
      if (net.isServerRequestError(err)) {
        AppDispatcher.dispatch({
          actionType: EventConstants.ENTRY_OPEN_FAILED,
          entryId,
        });
        if (deferred != null) {
          deferred.reject(err);
        } else {
          notifications.notifyError(err);
        }
      } else {
        throw err;
      }
    }
    return didSucceed;
  },

  close: (entryId: string): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.ENTRY_CLOSE,
      entryId,
    });
  },

  invalidate: (entryId: string): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.ENTRY_INVALIDATE,
      entryId,
    });
  },

  revalidate: (entryId: string): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.ENTRY_OPEN_FAILED,
      entryId,
      // Since revalidate is called when an ActionFunction is aborted, the entry will not be out of date,
      // unlike when we dispatch the same event when failing to reload or open the entry.
      clearDirtyState: true,
    });
  },

  registerCollaboratorJoined: (entryId: string, userId: string): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.ENTRY_COLLAB_JOIN,
      entryId,
      userId,
    });
  },

  registerCollaboratorLeft: (entryId: string, userId: string): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.ENTRY_COLLAB_LEAVE,
      entryId,
      userId,
    });
  },

  triggerEntryEvaluation: (fileId: string): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.EXCESS_FORCE_EVALUATION,
      fileId,
    });
  },

  generateEvaluationSnapshot: async (entryId: string): Promise<void> => {
    const EntryStore = require('stores/EntryStore').default;

    // Abort snapshot generation if entry is not in store
    // We do not expect this to happen
    if (!EntryStore.inStore(entryId)) {
      logging.error(`Attempted to generate evaluation snapshot for ${entryId} but it was not in the store!`);
      return;
    }

    const entry = EntryStore.getEntry(entryId);
    const note = entry.note;
    const fullTableMap = getNoteTableMap(note.id, note);
    const {computedTables} = await evaluateTablesWithResolutions({
      fullTableMap,
      hasFormulasDisabled: false,
      selectors: note.selectors,
    });
    AppDispatcher.dispatch({
      actionType: EventConstants.ENTRY_GENERATE_SNAPSHOT,
      entryId,
      evaluatedTables: computedTables,
    });
  },

  createFromTemplate: ({
    entryName,
    folderId,
    templateId,
    tagSchemaId,
    schemaTags,
    initialDay,
    studyAssociations,
    onSuccess,
    fulfillments,
  }: {
    entryName: string;
    folderId: string;
    templateId: string;
    tagSchemaId?: string;
    schemaTags?: TagUpdateObject;
    initialDay?: string;
    studyAssociations?: Array<StudyAssociation>;
    onSuccess?: () => void;
    fulfillments?: Array<RequestFulfillmentDefinition>;
  }): JQueryPromise<EntryWithFulfillments> => {
    LoadingScreen.show();
    return EntryDAO.createFromTemplate({
      name: entryName,
      folder: folderId,
      template: templateId,
      tagSchemaId,
      schemaTags,
      initialDay,
      studyAssociations,
      fulfillments,
    })
      .done((entryResponse: EntryWithFulfillments) => {
        dispatchFulfillmentsLoadedForEntry(entryResponse);
        FileBrowserActions.openFile(entryResponse.id);
        if (onSuccess != null) {
          onSuccess();
        }
      })
      .fail(notifications.notifyError)
      .always(() => {
        LoadingScreen.hide();
      });
  },

  /**
   * Make an empty RegistrationTable in the database so that we can hook up a NotePart to it.
   */
  createRegistrationTable: (
    name: string,
    tagSchemaId: string,
    tableType: RegistrationTableType,
    labelingStrategy: RegistryLabelingStrategy,
    {success, fileId}: {success: (registrationTable: RegistrationTable) => void; fileId: string}
  ): void => {
    LoadingScreen.show();

    const entryType = APIUtil.apiIdToType(fileId);
    const isStageEntry =
      entryType === ModelTypeConstants.STAGE_CONFIGURATION || entryType === ModelTypeConstants.STAGE_ENTRY;
    const isWatchingInputTable = isStageEntry;

    EntryDAO.createRegistrationTable({
      name,
      tagSchemaId,
      tableType,
      labelingStrategy,
      fileId,
      isWatchingInputTable,
    })
      .done(
        ({
          registrationTable,
          registrationRecordList,
        }: {
          registrationTable: RegistrationTable;
          registrationRecordList: RegistrationRecordList;
        }) => {
          analytics.deprecatedTrackEvent(ELN_ANALYTICS_ACTIONS.CREATED_REGISTRATION_TABLE_IN_ENTRY, {
            isWatchingInputTable: registrationTable.isWatchingInputTable,
            initialRowCount: registrationTable.rows.length,
            initialColumnCount: registrationTable.columns.length,
          });
          getStore().dispatch(
            ModelActions.registrationRecordListsFetchCompleted({
              [registrationRecordList.id]: registrationRecordList,
            })
          );
          success(registrationTable);
        }
      )
      .fail(notifications.notifyError)
      .always(() => {
        LoadingScreen.hide();
      });
  },

  invokeProcessTaskExecutionTableMapping: (entryId: string, taskIds: Array<string>): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.ENTRY_INVOKE_PROCESS_TASK_EXECUTION_TABLE_MAPPING,
      entryId,
      taskIds,
    });
  },

  dispatchNoteReferencesLoadedForEntry,
  dispatchBlobDetailsLoadedForEntry,

  setFocusedTable: (
    fileId: string,
    focusedTableProvenanceUuid: string | null,
    focusedTableViewMode: FocusedTableViewMode
  ): void => {
    AppDispatcher.dispatch({
      actionType: EventConstants.ENTRY_SET_FOCUSED_TABLE,
      fileId,
      focusedTableProvenanceUuid,
      focusedTableViewMode,
    });
  },
};

export default EntryActions;
